import React, { useState, useEffect, useMemo } from "react";
import { Accordion, Button, Spinner } from "react-bootstrap";
import Plus from "Components/Icons/Plus";
import Minus from "Components/Icons/Minus";
import { useCompetencyMatchingContext } from "@/contexts/competencyMatchingContext";
import { DomainCompetency } from "Stores/courseStore";
import { ArrowUp } from "lucide-react";

interface SavedMappings {
    [standardRuuid: string]: string[];
}

export const ReviewMatching = () => {
    const {
        handlePreviousStep,
        selectedStandardCategoryData,
        standardsCategoriesLoading,
    } = useCompetencyMatchingContext();

    const [activeKeys, setActiveKeys] = useState<string[]>([]);
    const [savedMappings, setSavedMappings] = useState<SavedMappings>({});

    useEffect(() => {
        const storedMappings = localStorage.getItem(
            "competencyMatchingCategories"
        );
        if (storedMappings) {
            try {
                setSavedMappings(JSON.parse(storedMappings));
            } catch (error) {
                console.error("Error parsing saved mappings:", error);
                setSavedMappings({});
            }
        }
    }, []);

    const handleAccordionToggle = (ruuid: string) => {
        setActiveKeys((prevKeys) =>
            prevKeys.includes(ruuid)
                ? prevKeys.filter((key) => key !== ruuid)
                : [...prevKeys, ruuid]
        );
    };

    const calculateMatchStatus = (itemRuuid: string, level: number): string => {
        if (level === 0) {
            return "100% Completed";
        }

        const mappedItems = savedMappings[itemRuuid];
        if (mappedItems && mappedItems.length > 0) {
            return `${mappedItems.length} ${
                mappedItems.length === 1 ? "competency" : "competencies"
            }`;
        }
        return "";
    };

    const renderCompetencyItem = (item: DomainCompetency, level = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const isOpen = activeKeys.includes(item.ruuid);
        const matchStatus = calculateMatchStatus(item.ruuid, level);

        const hasAnyMapping = (currentItem: DomainCompetency): boolean => {
            if (savedMappings[currentItem.ruuid]?.length > 0) {
                return true;
            }
            if (currentItem.children && currentItem.children.length > 0) {
                return currentItem.children.some(hasAnyMapping);
            }
            return false;
        };

        if (level > 0 && !hasAnyMapping(item)) {
            return null;
        }

        return (
            <div key={item.ruuid} className="competency-item">
                <div
                    className="competency-item-inner"
                    style={{ paddingLeft: `${level * 20}px` }}
                >
                    <Accordion.Item eventKey={item.ruuid} className="border-0">
                        <div className="d-flex align-items-center competency-header py-2">
                            {hasChildren ? (
                                <Button
                                    variant="link"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAccordionToggle(item.ruuid);
                                    }}
                                    className="p-0 me-2 toggle-button"
                                    aria-expanded={isOpen}
                                >
                                    {isOpen ? <Minus /> : <Plus />}
                                </Button>
                            ) : (
                                <span
                                    className="toggle-placeholder me-2"
                                    style={{
                                        width: "16px",
                                        display: "inline-block",
                                    }}
                                ></span>
                            )}
                            <span className="competency-name flex-grow-1">
                                {item.standard_name}
                            </span>
                            <div className="match-status text-end">
                                {matchStatus && (
                                    <span className="badge bg-light text-dark border rounded-pill px-3 py-1">
                                        {matchStatus}
                                    </span>
                                )}
                            </div>
                        </div>
                        {hasChildren && (
                            <Accordion.Collapse eventKey={item.ruuid}>
                                <div className="competency-children">
                                    {item.children.map((child) =>
                                        renderCompetencyItem(child, level + 1)
                                    )}
                                </div>
                            </Accordion.Collapse>
                        )}
                    </Accordion.Item>
                </div>
            </div>
        );
    };

    const competencyList = useMemo(() => {
        if (selectedStandardCategoryData) {
            return renderCompetencyItem(selectedStandardCategoryData);
        }
        return null;
    }, [selectedStandardCategoryData, activeKeys, savedMappings]);

    if (standardsCategoriesLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div data-testid="review-matching" className="w-100">
            <label className="section-title mb-4">Review Your Matches</label>
            <div className="competency-list">
                <div className="competency-header-row py-2 px-3">
                    <div className="row w-100">
                        <div className="col-8">Standard Competency</div>
                        <div className="col-4 text-end">Match</div>
                    </div>
                </div>
                <Accordion
                    activeKey={activeKeys}
                    className="competency-accordion"
                >
                    {competencyList ?? (
                        <div className="text-center p-3">
                            No competency data available.
                        </div>
                    )}
                </Accordion>
            </div>

            <div className="form-actions form-actions--matching mt-4">
                <Button
                    variant="outline-secondary"
                    onClick={handlePreviousStep}
                >
                    Back
                </Button>
                <Button
                    variant="primary"
                    className="btn-continue"
                    onClick={() => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                >
                    Back to top
                    <ArrowUp className="ms-2" />
                </Button>
            </div>
        </div>
    );
};
